import React, { useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { Container, Row, Col, Card, Form, Button, Table, Badge, Alert, ProgressBar, Tabs, Tab } from "react-bootstrap";
import { FaPlus, FaEdit, FaTrash, FaArrowLeft, FaSearch, FaFileExcel, FaDownload, FaUpload, FaCheck, FaTimes, FaCheckCircle, FaExclamationTriangle, FaClipboardList } from "react-icons/fa";
import axios from "axios";
import ModalConfirmacion from "../../../components/modals/ModalConfirmacion";

const API_URL = process.env.REACT_APP_PYTHON_API_URL || "http://localhost:8003";

const MantenimientoFamiliasInsumos = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  // Estados principales
  const [familias, setFamilias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [state, setState] = useState({
    familiaSeleccionada: null,
    filtro: "",
  });

  // Tab principal
  const [activeMainTab, setActiveMainTab] = useState('individual'); // 'individual' o 'masiva'

  // Modal CRUD (solo para tab individual)
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' o 'edit'
  const [formData, setFormData] = useState({ nombre: '' });

  // Modal de confirmación de eliminación
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [familiaToDelete, setFamiliaToDelete] = useState(null);

  // Carga Masiva (para tab masiva)
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [uploadStep, setUploadStep] = useState("select"); // 'select', 'validating', 'validated', 'uploading', 'success'

  // ========== Cargar familias ==========
  const fetchFamilias = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/mantenimientos/planificacion-insumos/familias-insumos/`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { with_stats: true }
      });
      setFamilias(response.data);
    } catch (error) {
      console.error("Error al cargar familias:", error);
      toast.error("Error al cargar familias de insumos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFamilias();
  }, []);

  // Filtrar familias
  const familiasFiltradas = familias.filter((familia) =>
    familia.nombre.toLowerCase().includes(state.filtro.toLowerCase())
  );

  // ========== CRUD Handlers ==========
  const handleOpenCreateModal = () => {
    setModalMode('create');
    setFormData({ nombre: '' });
    setShowModal(true);
  };

  const handleOpenEditModal = (familia) => {
    setModalMode('edit');
    setFormData({ id: familia.id, nombre: familia.nombre });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({ nombre: '' });
  };

  const handleSaveFamilia = async () => {
    if (!formData.nombre || formData.nombre.trim().length < 3) {
      toast.error("El nombre debe tener al menos 3 caracteres");
      return;
    }

    setLoading(true);
    try {
      if (modalMode === 'create') {
        await axios.post(
          `${API_URL}/api/mantenimientos/planificacion-insumos/familias-insumos/`,
          { nombre: formData.nombre.trim() },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Familia de insumos creada exitosamente");
      } else {
        await axios.put(
          `${API_URL}/api/mantenimientos/planificacion-insumos/familias-insumos/${formData.id}`,
          { nombre: formData.nombre.trim() },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Familia de insumos actualizada exitosamente");
      }
      fetchFamilias();
      handleCloseModal();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Error al guardar familia de insumos";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFamilia = (familia) => {
    if (!familia) {
      toast.error("Por favor seleccione una familia para eliminar");
      return;
    }

    // Validación previa: verificar si tiene subfamilias asociadas
    // El backend garantiza que total_subfamilias siempre es un entero >= 0
    if (familia.total_subfamilias > 0) {
      toast.error(
        `No se puede eliminar la familia "${familia.nombre}" porque tiene ${familia.total_subfamilias} subfamilia${familia.total_subfamilias > 1 ? 's' : ''} asociada${familia.total_subfamilias > 1 ? 's' : ''}. ` +
        `Por favor, elimine primero las subfamilias asociadas.`
      );
      return;
    }

    setFamiliaToDelete(familia);
    setShowConfirmModal(true);
  };

  const confirmDeleteFamilia = async () => {
    if (!familiaToDelete) return;

    setLoading(true);
    try {
      await axios.delete(
        `${API_URL}/api/mantenimientos/planificacion-insumos/familias-insumos/${familiaToDelete.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Familia de insumos eliminada exitosamente");
      setState({ ...state, familiaSeleccionada: null });
      fetchFamilias();
      setShowConfirmModal(false);
      setFamiliaToDelete(null);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Error al eliminar familia de insumos";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // ========== Carga Masiva ==========
  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/mantenimientos/planificacion-insumos/familias-insumos/plantilla/excel`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "plantilla_familias_insumos.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success("Plantilla descargada exitosamente");
    } catch (error) {
      toast.error("Error al descargar plantilla");
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith(".xlsx") && !file.name.endsWith(".xls")) {
        toast.error("El archivo debe ser formato Excel (.xlsx o .xls)");
        return;
      }
      setSelectedFile(file);
      setUploadStep("select");
      setValidationResult(null);
    }
  };

  const handleValidateFile = async () => {
    if (!selectedFile) {
      toast.error("Por favor seleccione un archivo");
      return;
    }

    setUploadLoading(true);
    setUploadStep("validating");

    try {
      const formDataUpload = new FormData();
      formDataUpload.append("file", selectedFile);

      const response = await axios.post(
        `${API_URL}/api/mantenimientos/planificacion-insumos/familias-insumos/carga-masiva/validar`,
        formDataUpload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      setValidationResult(response.data);
      setUploadStep("validated");

      if (response.data.filas_validas > 0) {
        toast.success(
          `Validación exitosa: ${response.data.filas_validas} filas válidas de ${response.data.total_filas}`
        );
      } else {
        toast.warning("No hay filas válidas para cargar");
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Error al validar archivo";
      toast.error(errorMsg);
      setUploadStep("select");
    } finally {
      setUploadLoading(false);
    }
  };

  const handleConfirmUpload = async () => {
    if (!selectedFile || !validationResult || validationResult.filas_validas === 0) {
      toast.error("No hay datos válidos para cargar");
      return;
    }

    setUploadLoading(true);
    setUploadStep("uploading");

    try {
      const formDataUpload = new FormData();
      formDataUpload.append("file", selectedFile);

      const response = await axios.post(
        `${API_URL}/api/mantenimientos/planificacion-insumos/familias-insumos/carga-masiva/confirmar`,
        formDataUpload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      toast.success(response.data.message);
      setUploadStep("success");
      fetchFamilias();

      // Resetear después de 2 segundos
      setTimeout(() => {
        handleResetUpload();
      }, 2000);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Error en carga masiva";
      toast.error(errorMsg);
      setUploadStep("validated");
    } finally {
      setUploadLoading(false);
    }
  };

  const handleResetUpload = () => {
    setSelectedFile(null);
    setValidationResult(null);
    setUploadStep("select");
  };

  return (
    <>
      {/* Digital Harmony Styles */}
      <style>
        {`
          .dh-maintenance-bg {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            min-height: 100vh;
          }

          .dh-main-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 1.25rem 0;
            margin-bottom: 1.5rem;
            border-radius: 0 0 30px 30px;
            box-shadow: 0 10px 30px rgba(102, 126, 234, 0.2);
          }

          .dh-main-title {
            font-size: 1.75rem;
            font-weight: 700;
            margin: 0;
          }

          .dh-main-subtitle {
            font-size: 0.95rem;
            opacity: 0.9;
            margin: 0.5rem 0 0 0;
          }

          .dh-card {
            border: none;
            border-radius: 20px;
            box-shadow: 0 15px 35px rgba(0,0,0,0.1);
            background: white;
          }

          .dh-card-header {
            border-radius: 20px 20px 0 0;
            padding: 1rem 1.5rem;
            border: none;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            font-weight: 700;
            font-size: 0.95rem;
          }

          .btn-dh-primary {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            border: none;
            color: white;
            border-radius: 10px;
            padding: 8px 16px;
            font-weight: 600;
            font-size: 0.875rem;
            box-shadow: 0 4px 14px rgba(17, 153, 142, 0.3);
            transition: all 0.3s ease;
          }

          .btn-dh-primary:hover {
            background: linear-gradient(135deg, #38ef7d 0%, #2dd4bf 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(17, 153, 142, 0.4);
            color: white;
          }

          .btn-dh-warning {
            background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
            border: none;
            color: white;
            border-radius: 10px;
            padding: 8px 16px;
            font-weight: 600;
            font-size: 0.875rem;
            box-shadow: 0 4px 14px rgba(251, 191, 36, 0.3);
            transition: all 0.3s ease;
          }

          .btn-dh-warning:hover {
            background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(251, 191, 36, 0.4);
            color: white;
          }

          .btn-dh-danger {
            background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
            border: none;
            color: white;
            border-radius: 10px;
            padding: 8px 16px;
            font-weight: 600;
            font-size: 0.875rem;
            box-shadow: 0 4px 14px rgba(239, 68, 68, 0.3);
            transition: all 0.3s ease;
          }

          .btn-dh-danger:hover {
            background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(239, 68, 68, 0.4);
            color: white;
          }

          .btn-dh-secondary {
            background: linear-gradient(135deg, #6b7280 0%, #374151 100%);
            border: none;
            color: white;
            border-radius: 10px;
            padding: 8px 16px;
            font-weight: 600;
            font-size: 0.875rem;
            box-shadow: 0 4px 14px rgba(107, 114, 128, 0.3);
            transition: all 0.3s ease;
          }

          .btn-dh-secondary:hover {
            background: linear-gradient(135deg, #374151 0%, #1f2937 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(107, 114, 128, 0.4);
            color: white;
          }

          .btn-dh-info {
            background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
            border: none;
            color: white;
            border-radius: 10px;
            padding: 8px 16px;
            font-weight: 600;
            font-size: 0.875rem;
            box-shadow: 0 4px 14px rgba(59, 130, 246, 0.3);
            transition: all 0.3s ease;
          }

          .btn-dh-info:hover {
            background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(59, 130, 246, 0.4);
            color: white;
          }

          .dh-form-control {
            border: 2px solid #e5e7eb;
            border-radius: 8px;
            padding: 8px 12px;
            font-size: 0.875rem;
            transition: all 0.3s ease;
            background: rgba(255, 255, 255, 0.9);
          }

          .dh-form-control:focus {
            border-color: #667eea;
            box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
            background: white;
          }

          .dh-enhanced-table {
            border-collapse: separate;
            border-spacing: 0;
            border-radius: 15px;
            overflow: hidden;
            box-shadow: 0 4px 15px rgba(0,0,0,0.05);
            background: white;
          }

          .dh-enhanced-table thead th {
            background: #374151 !important;
            color: white !important;
            font-weight: 700 !important;
            font-size: 0.7rem !important;
            text-transform: uppercase !important;
            padding: 0.5rem 0.4rem !important;
            border: none !important;
            position: sticky !important;
            top: 0 !important;
            z-index: 10 !important;
          }

          .dh-enhanced-table tbody td {
            padding: 0.4rem !important;
            border: none;
            border-bottom: 1px solid #f3f4f6;
            font-size: 0.75rem;
            vertical-align: middle;
          }

          .dh-enhanced-table tbody tr:hover {
            background: linear-gradient(135deg, #f9fafb 0%, rgba(102, 126, 234, 0.02) 100%) !important;
          }

          .dh-enhanced-table tbody tr.selected {
            background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%) !important;
            border-left: 4px solid #667eea;
          }

          .dh-checkbox {
            width: 20px;
            height: 20px;
            border: 2px solid #d1d5db;
            border-radius: 6px;
            background: white;
            cursor: pointer;
            transition: all 0.2s ease;
          }

          .dh-checkbox:checked {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border-color: #667eea;
          }

          .dh-badge-info {
            background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
            color: white;
            border-radius: 8px;
            padding: 4px 10px;
            font-weight: 600;
            font-size: 0.75rem;
          }

          .nav-tabs {
            margin-bottom: 0.75rem;
            margin-top: 0;
          }
        `}
      </style>

      <div className="dh-maintenance-bg">
        <ToastContainer />

        <Container fluid className="py-4">
          <Row>
            <Col lg={12}>
              <Card className="dh-card">
                <Card.Header className="dh-card-header">
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Mantenimiento Familias Insumos</h5>
                    <Button
                      className="btn-dh-secondary d-flex align-items-center"
                      onClick={() => navigate("/home")}
                      style={{ background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)', border: 'none', color: 'white', fontSize: '0.8rem', padding: '6px 12px' }}
                    >
                      <FaArrowLeft className="me-2" />
                      Volver
                    </Button>
                  </div>
                </Card.Header>
                <Card.Body style={{ padding: '1rem' }}>
                  <Tabs
                    activeKey={activeMainTab}
                    onSelect={(k) => setActiveMainTab(k)}
                    className="mb-4"
                  >
                    {/* TAB 1: Mantenimiento Individual */}
                    <Tab
                      eventKey="individual"
                      title={
                        <span>
                          <FaClipboardList className="me-2" />
                          Mantenimiento Individual
                        </span>
                      }
                    >
                      {/* Botones de acción */}
                      <div className="d-flex justify-content-end gap-2 mb-4">
                        <Button
                          onClick={handleOpenCreateModal}
                          className="btn-dh-primary d-flex align-items-center"
                        >
                          <FaPlus className="me-2" />
                          Nuevo
                        </Button>
                        <Button
                          className="btn-dh-warning d-flex align-items-center"
                          onClick={() => {
                            if (state.familiaSeleccionada) {
                              handleOpenEditModal(state.familiaSeleccionada);
                            } else {
                              toast.error("Por favor seleccione una familia para editar");
                            }
                          }}
                        >
                          <FaEdit className="me-2" />
                          Editar
                        </Button>
                        <Button
                          className="btn-dh-danger d-flex align-items-center"
                          onClick={() => handleDeleteFamilia(state.familiaSeleccionada)}
                          disabled={!state.familiaSeleccionada}
                        >
                          <FaTrash className="me-2" />
                          Eliminar
                        </Button>
                      </div>

                      {/* Búsqueda */}
                      <div className="mb-4">
                        <div className="position-relative">
                          <Form.Control
                            type="search"
                            className="dh-form-control"
                            placeholder="Buscar familia de insumos..."
                            value={state.filtro}
                            onChange={(e) => setState({ ...state, filtro: e.target.value })}
                            style={{ paddingLeft: '3rem' }}
                          />
                          <FaSearch
                            className="position-absolute"
                            style={{
                              left: '1rem',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              color: '#6b7280'
                            }}
                          />
                        </div>
                      </div>

                      {/* Tabla */}
                      <div className="table-responsive" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                        <Table className="dh-enhanced-table">
                          <thead>
                            <tr>
                              <th style={{ width: "50px" }}></th>
                              <th>Nombre</th>
                              <th className="text-center" style={{ width: "150px" }}>Subfamilias</th>
                            </tr>
                          </thead>
                          <tbody>
                            {loading ? (
                              <tr>
                                <td colSpan="3" className="text-center py-5">
                                  <div className="d-flex flex-column align-items-center">
                                    <div className="spinner-border text-primary mb-3" role="status">
                                      <span className="visually-hidden">Cargando...</span>
                                    </div>
                                    <span className="text-muted">Cargando familias de insumos...</span>
                                  </div>
                                </td>
                              </tr>
                            ) : familiasFiltradas.length === 0 ? (
                              <tr>
                                <td colSpan="3" className="text-center py-5">
                                  <Alert variant="info" className="mb-0">
                                    <FaFileExcel className="me-2" />
                                    No se encontraron familias de insumos
                                  </Alert>
                                </td>
                              </tr>
                            ) : (
                              familiasFiltradas.map((familia) => (
                                <tr
                                  key={familia.id}
                                  className={state.familiaSeleccionada?.id === familia.id ? 'selected' : ''}
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => setState({ ...state, familiaSeleccionada: familia })}
                                >
                                  <td>
                                    <Form.Check
                                      type="checkbox"
                                      className="dh-checkbox"
                                      checked={state.familiaSeleccionada?.id === familia.id}
                                      onChange={() => {}}
                                    />
                                  </td>
                                  <td style={{ fontWeight: '600', color: '#374151' }}>{familia.nombre}</td>
                                  <td className="text-center">
                                    <Badge className="dh-badge-info">
                                      {familia.total_subfamilias || 0}
                                    </Badge>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </Table>
                      </div>
                    </Tab>

                    {/* TAB 2: Carga Masiva */}
                    <Tab
                      eventKey="masiva"
                      title={
                        <span>
                          <FaFileExcel className="me-2" />
                          Carga Masiva
                        </span>
                      }
                    >
                      {uploadStep === "select" && (
                        <>
                          <div className="d-flex justify-content-between align-items-center mb-4">
                            <Alert variant="info" className="mb-0 flex-grow-1 me-3" style={{ fontSize: '0.875rem' }}>
                              <h6 className="fw-bold mb-2" style={{ fontSize: '0.95rem' }}>
                                <FaCheckCircle className="me-2" />
                                Instrucciones para Carga Masiva
                              </h6>
                              <ol className="mb-0" style={{ fontSize: '0.85rem', paddingLeft: '1.25rem' }}>
                                <li className="mb-1">Descargue la plantilla Excel usando el botón</li>
                                <li className="mb-1">Complete los datos en el archivo Excel (columna: <strong>nombre</strong>)</li>
                                <li className="mb-1">Guarde el archivo en su computadora</li>
                                <li className="mb-1">Seleccione el archivo completado</li>
                                <li>Valide los datos y confirme la carga</li>
                              </ol>
                            </Alert>

                            <Button
                              className="btn-dh-primary"
                              onClick={handleDownloadTemplate}
                              style={{ minWidth: '200px', height: 'fit-content' }}
                            >
                              <FaDownload className="me-2" />
                              Descargar Template
                            </Button>
                          </div>

                          <Card style={{ border: '2px solid #3b82f6', borderRadius: '15px' }}>
                            <Card.Body className="text-center py-3">
                              <FaUpload size={35} className="text-primary mb-2" />
                              <h6 className="fw-bold mb-2" style={{ fontSize: '0.95rem' }}>Seleccionar Archivo Excel</h6>
                              <p className="text-muted mb-3" style={{ fontSize: '0.85rem' }}>Suba el archivo Excel completado para validar</p>
                              <div className="d-flex justify-content-center">
                                <Form.Control
                                  type="file"
                                  className="dh-form-control"
                                  accept=".xlsx,.xls"
                                  onChange={handleFileSelect}
                                  style={{ maxWidth: '500px' }}
                                />
                              </div>
                              {selectedFile && (
                                <Alert variant="success" className="mt-2 mb-0 mx-auto" style={{ maxWidth: '500px', fontSize: '0.85rem' }}>
                                  <FaCheckCircle className="me-2" />
                                  <strong>{selectedFile.name}</strong>
                                </Alert>
                              )}
                            </Card.Body>
                          </Card>

                          {selectedFile && (
                            <div className="text-center mt-3">
                              <Button
                                className="btn-dh-info"
                                onClick={handleValidateFile}
                                disabled={uploadLoading}
                              >
                                <FaCheckCircle className="me-2" />
                                Validar Archivo
                              </Button>
                            </div>
                          )}
                        </>
                      )}

                      {uploadStep === "validating" && (
                        <div className="text-center py-4">
                          <div className="spinner-border text-primary mb-2" role="status" style={{ width: '2rem', height: '2rem' }}>
                            <span className="visually-hidden">Validando...</span>
                          </div>
                          <h6 className="mb-2" style={{ fontSize: '0.95rem' }}>Validando archivo Excel...</h6>
                          <p className="text-muted" style={{ fontSize: '0.85rem' }}>Por favor espere mientras se validan los datos</p>
                        </div>
                      )}

                      {uploadStep === "validated" && validationResult && (
                        <>
                          <Alert variant={validationResult.filas_validas > 0 ? "success" : "warning"} style={{ fontSize: '0.875rem' }}>
                            <h6 className="fw-bold mb-2" style={{ fontSize: '0.95rem' }}>
                              <FaCheckCircle className="me-2" />
                              Resultado de Validación
                            </h6>
                            <Row className="text-center">
                              <Col md={4}>
                                <div className="p-2">
                                  <h4 className="mb-0" style={{ fontSize: '1.5rem' }}>{validationResult.total_filas}</h4>
                                  <small className="text-muted" style={{ fontSize: '0.8rem' }}>Total Filas</small>
                                </div>
                              </Col>
                              <Col md={4}>
                                <div className="p-2">
                                  <h4 className="mb-0 text-success" style={{ fontSize: '1.5rem' }}>{validationResult.filas_validas}</h4>
                                  <small className="text-muted" style={{ fontSize: '0.8rem' }}>Filas Válidas</small>
                                </div>
                              </Col>
                              <Col md={4}>
                                <div className="p-2">
                                  <h4 className="mb-0 text-danger" style={{ fontSize: '1.5rem' }}>{validationResult.filas_invalidas}</h4>
                                  <small className="text-muted" style={{ fontSize: '0.8rem' }}>Filas Inválidas</small>
                                </div>
                              </Col>
                            </Row>
                          </Alert>

                          {validationResult.errores.length > 0 && (
                            <Alert variant="danger" style={{ fontSize: '0.875rem' }}>
                              <h6 className="fw-bold mb-2" style={{ fontSize: '0.9rem' }}>
                                <FaExclamationTriangle className="me-2" />
                                Errores encontrados ({validationResult.errores.length})
                              </h6>
                              <div style={{ maxHeight: '250px', overflowY: 'auto' }} className="mt-2">
                                <Table size="sm" striped bordered className="mb-0" style={{ fontSize: '0.8rem' }}>
                                  <thead>
                                    <tr>
                                      <th style={{ width: '80px' }}>Fila</th>
                                      <th style={{ width: '120px' }}>Campo</th>
                                      <th>Error</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {validationResult.errores.map((error, idx) => (
                                      <tr key={idx}>
                                        <td className="text-center"><Badge bg="danger">{error.fila}</Badge></td>
                                        <td><code>{error.campo}</code></td>
                                        <td>{error.error}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </Table>
                              </div>
                            </Alert>
                          )}

                          <div className="d-flex justify-content-center gap-3 mt-3">
                            <Button className="btn-dh-secondary" onClick={handleResetUpload}>
                              <FaTimes className="me-2" />
                              Seleccionar otro archivo
                            </Button>
                            {validationResult.filas_validas > 0 && (
                              <Button
                                className="btn-dh-primary"
                                onClick={handleConfirmUpload}
                                disabled={uploadLoading}
                              >
                                <FaUpload className="me-2" />
                                Confirmar Carga ({validationResult.filas_validas} filas)
                              </Button>
                            )}
                          </div>
                        </>
                      )}

                      {uploadStep === "uploading" && (
                        <div className="text-center py-4">
                          <div className="spinner-border text-success mb-2" role="status" style={{ width: '2rem', height: '2rem' }}>
                            <span className="visually-hidden">Cargando...</span>
                          </div>
                          <h6 className="mb-2" style={{ fontSize: '0.95rem' }}>Cargando datos al sistema...</h6>
                          <p className="text-muted" style={{ fontSize: '0.85rem' }}>Insertando {validationResult?.filas_validas} registros</p>
                          <ProgressBar animated now={100} className="mt-2" style={{ height: '18px' }} />
                        </div>
                      )}

                      {uploadStep === "success" && (
                        <Alert variant="success" className="text-center py-3" style={{ fontSize: '0.875rem' }}>
                          <FaCheckCircle size={40} className="text-success mb-2" />
                          <h5 className="fw-bold mb-2" style={{ fontSize: '1rem' }}>¡Carga completada exitosamente!</h5>
                          <p className="mb-2" style={{ fontSize: '0.85rem' }}>Los datos han sido insertados correctamente en el sistema</p>
                          <Button className="btn-dh-primary" onClick={handleResetUpload}>
                            <FaPlus className="me-2" />
                            Cargar otro archivo
                          </Button>
                        </Alert>
                      )}
                    </Tab>
                  </Tabs>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Modal CRUD (solo para registro individual) */}
      {showModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content" style={{ borderRadius: '20px', overflow: 'hidden' }}>
              <div className="modal-header" style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none'
              }}>
                <h5 className="modal-title fw-bold">
                  <FaFileExcel className="me-2" />
                  {modalMode === 'create' ? 'Nueva Familia de Insumos' : 'Editar Familia de Insumos'}
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={handleCloseModal}
                ></button>
              </div>
              <div className="modal-body" style={{ padding: '2rem' }}>
                <Form onSubmit={(e) => { e.preventDefault(); handleSaveFamilia(); }}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold" style={{ fontSize: '1.1rem' }}>
                      <FaFileExcel className="me-2 text-primary" />
                      Nombre de la Familia *
                    </Form.Label>
                    <Form.Control
                      type="text"
                      className="dh-form-control"
                      placeholder="Ej: Abarrotes, Carnes, Lácteos, Verduras y Hortalizas"
                      value={formData.nombre || ''}
                      onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                      required
                      maxLength={100}
                      style={{ fontSize: '1.1rem' }}
                    />
                    <Form.Text className="text-muted">
                      Mínimo 3 caracteres, máximo 100 caracteres
                    </Form.Text>
                  </Form.Group>

                  <Alert variant="info">
                    <strong>💡 Sugerencia:</strong> Las familias de insumos agrupan productos similares.
                    Ejemplos: Abarrotes, Carnes, Lácteos, Verduras, Grasas y Aceites, Condimentos.
                  </Alert>
                </Form>
              </div>
              <div className="modal-footer" style={{ borderTop: '2px solid #e5e7eb', padding: '1.5rem' }}>
                <Button className="btn-dh-secondary" onClick={handleCloseModal}>
                  <FaTimes className="me-2" />
                  Cancelar
                </Button>
                <Button
                  className="btn-dh-primary"
                  onClick={handleSaveFamilia}
                  disabled={!formData.nombre || formData.nombre.trim().length < 3 || loading}
                >
                  <FaCheck className="me-2" />
                  {modalMode === 'create' ? 'Crear' : 'Actualizar'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación de eliminación */}
      <ModalConfirmacion
        show={showConfirmModal}
        onHide={() => {
          setShowConfirmModal(false);
          setFamiliaToDelete(null);
        }}
        onConfirm={confirmDeleteFamilia}
        title="Confirmar Eliminación"
        message={
          familiaToDelete
            ? `¿Está seguro de eliminar la familia "${familiaToDelete.nombre}"?`
            : ""
        }
        confirmText="Eliminar"
        cancelText="Cancelar"
        loading={loading}
        variant="danger"
      />
    </>
  );
};

export default MantenimientoFamiliasInsumos;
